const isObj = (v) => v !== null && typeof v === "object";

const stripHtml = (s = "") => String(s).replace(/<[^>]*>/g, "");

const fillTemplateWithTokens = (html = "", toks = []) => {
  const plain = stripHtml(html);
  let i = 0;
  // اضافه کردن نمایش جداگانه با |
  const joined = toks.map(String).join(",");
  return `(${joined})`;
};

const hasSelected = (v) =>
  v != null && typeof v === "object" && !Array.isArray(v) && "selected" in v;

// استخراج ترتیب کلیدهای {{k}} برای c_test
const extractCtestKeyOrder = (html = "") => {
  const rx = /\{\{(\w+)\}\}/g;
  const out = [];
  let m;
  while ((m = rx.exec(html))) out.push(String(m[1]));
  return out;
};

const renderChoice = (item, key, lang = "en", t) => {
  if (key == null) return t("exam_result_page.placeholder");
  const opt = item?.body?.options?.find?.((o) => o.key === key);
  if (!opt) return String(key);
  const lbl = opt.label?.[lang] || opt.label?.fa || "";
  const clean = stripHtml(lbl).trim();
  return clean ? `${key} — ${clean}` : String(key);
};

export const formatAnswerForDisplay = (item, val, t) => {
  const kind = item?.kind || "";

  if (kind === "cloze_text" || kind === "cloze_paragraph") {
    // انتظار: val = { "1": "user1", "2": "user2", ... }
    if (!val || typeof val !== "object") {
      return t("exam_result_page.placeholder");
    }

    const textHtml =
      item?.body?.text?.en || item?.body?.text?.fa || item?.body?.text || "";
    const rx = /\{\{(\w+)\}\}/g;
    const keys = [];
    let m;
    while ((m = rx.exec(textHtml))) {
      keys.push(m[1]);
    }

    if (!keys.length) {
      // اگر به هر دلیل placeholder پیدا نشد، fallback
      try {
        return JSON.stringify(val);
      } catch {
        return t("exam_result_page.placeholder");
      }
    }

    const parts = keys.map((k) => {
      const v = val[k];
      return `${k}: ${v != null && String(v).trim() ? String(v) : "…"}`;
    });

    return parts.join(" | ");
  }

  if (kind === "file_upload") {
    if (!val) return t("exam_result_page.placeholder");

    const extractFileInfo = (v) => {
      if (!v) return null;
      // اگر رشته باشد، فرض می‌کنیم url است
      if (typeof v === "string") {
        const parts = v.split("/");
        const name = parts[parts.length - 1] || v;
        return { name, url: v };
      }
      // ساختار پیشنهادی: { file: { ... } }
      if (isObj(v.file)) return v.file;

      // یا مستقیم { name, url, size, type }
      if (isObj(v)) {
        if (v.url || v.name) {
          return {
            name: v.name,
            url: v.url || v.href,
            size: v.size,
            type: v.type || v.mime,
          };
        }
      }
      return null;
    };

    const info = extractFileInfo(val);
    if (!info) return t("exam_result_page.placeholder");

    const sizePart =
      typeof info.size === "number" && info.size > 0
        ? ` (${Math.round(info.size / 1024)} KB)`
        : "";

    return `${
      info.name || info.url || t("exam_result_page.placeholder")
    }${sizePart}`;
  }

  if (kind === "select_sentence") {
    const body = item.body || {};
    const sentences = Array.isArray(body.sentences) ? body.sentences : [];

    // خواندن پاسخ کاربر
    let ids = [];

    if (typeof val === "string" || typeof val === "number") {
      ids = [String(val)];
    } else if (Array.isArray(val)) {
      ids = val.map((x) => String(x));
    } else if (val && typeof val === "object") {
      if (Array.isArray(val.selectedIds)) {
        ids = val.selectedIds.map((x) => String(x));
      } else if (Array.isArray(val.selected)) {
        ids = val.selected.map((x) => String(x));
      } else if (val.selectedId != null) {
        ids = [String(val.selectedId)];
      }
    }

    if (!ids.length || !sentences.length) return "";

    const textMap = new Map(
      sentences.map((s) => [
        String(s.id),
        s?.text?.fa || s?.fa || s?.text?.en || s?.en || String(s.id),
      ])
    );

    const chosen = ids.map((id) => textMap.get(String(id))).filter(Boolean);

    // چند تا انتخاب رو با " | " جدا می‌کنیم که توی کارنامه واضح باشه
    return chosen.join(" | ");
  }

  if (kind === "numeric") {
    if (val === null || val === undefined) {
      return t("exam_result_page.placeholder");
    }

    // اگر مستقیم عدد یا رشته بود
    if (typeof val === "number" || typeof val === "string") {
      const s = String(val).trim();
      return s === "" ? t("exam_result_page.placeholder") : s;
    }

    // اگر آبجکت بود: { value: ... } یا { answer: ... }
    if (isObj(val)) {
      const inner = val.value ?? val.answer;
      if (inner === null || inner === undefined) {
        return t("exam_result_page.placeholder");
      }
      const s = String(inner).trim();
      return s === "" ? t("exam_result_page.placeholder") : s;
    }

    return t("exam_result_page.placeholder");
  }

  if (kind === "sentence_builder") {
    const body = item.body || {};
    const tokens = Array.isArray(body.tokens) ? body.tokens : [];

    let seq = [];
    if (Array.isArray(val)) {
      seq = val;
    } else if (val && typeof val === "object") {
      if (Array.isArray(val.tokens)) seq = val.tokens;
      else if (Array.isArray(val.sequence)) seq = val.sequence;
    }

    if (!seq.length || !tokens.length) return "";

    const labelMap = new Map(
      tokens.map((tk) => [
        String(tk.key),
        tk?.label?.fa || tk?.fa || tk?.label?.en || tk?.en || String(tk.key),
      ])
    );

    const words = seq.map((k) => {
      const key = String(k);
      return labelMap.get(key) || key;
    });

    return words.join(" ");
  }

  if (kind === "drag_drop_item_target") {
    const body = item.body || {};
    const items = Array.isArray(body.items) ? body.items : [];
    const targets = Array.isArray(body.targets) ? body.targets : [];

    if (!items.length) return "";

    const targetLabelMap = new Map(
      targets.map((t, idx) => {
        const key = String(t.key || `T${idx + 1}`);
        const label = t?.label?.fa || t?.fa || t?.label?.en || t?.en || key;
        return [key, String(label)];
      })
    );

    const rawVal = val && typeof val === "object" ? val : {};
    const userMap =
      rawVal.assignments && typeof rawVal.assignments === "object"
        ? rawVal.assignments
        : rawVal;

    const parts = items.map((it, idx) => {
      const itemKey = String(it.key || `I${idx + 1}`);
      const itemLabel =
        it?.label?.fa || it?.fa || it?.label?.en || it?.en || itemKey;

      const chosenKey = userMap[itemKey];
      if (!chosenKey) {
        return `${String(itemLabel)} → —`;
      }

      const chosenLabel =
        targetLabelMap.get(String(chosenKey)) || String(chosenKey);

      return `${String(itemLabel)} → ${String(chosenLabel)}`;
    });

    return parts.join(" | ");
  }

  if (kind === "short_text") {
    if (val == null) return t("exam_result_page.placeholder");

    if (
      typeof val === "string" ||
      typeof val === "number" ||
      typeof val === "boolean"
    ) {
      return String(val);
    }

    if (isObj(val) && (val.text != null || val.value != null)) {
      const out = String(val.text ?? val.value ?? "");
      return out || t("exam_result_page.placeholder");
    }

    return t("exam_result_page.placeholder");
  }

  if (kind === "word_bank") {
    const body = item?.body || {};
    const bank = Array.isArray(body.bank) ? body.bank : [];

    const bankMap = new Map(
      bank.map((w, idx) => {
        const key = w.key || `w${idx + 1}`;
        const label = w?.label?.fa?.trim?.() || w?.label?.en?.trim?.() || key;
        return [key, label];
      })
    );

    if (val && typeof val === "object") {
      const keys = Object.keys(val);
      if (!keys.length) return t("exam_result_page.placeholder");

      // اگر keyها عددی هستند، مرتب‌سازی بر اساس عدد
      keys.sort((a, b) => {
        const na = Number(a);
        const nb = Number(b);
        if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
        return String(a).localeCompare(String(b));
      });

      const parts = keys.map((k) => {
        const bankKey = val[k];
        const lbl = bankMap.get(bankKey) || bankKey || "—";
        return `${k}: ${lbl}`;
      });

      return parts.join(" | ");
    }

    return t("exam_result_page.placeholder");
  }

  if (kind === "cloze_regex") {
    if (!val || typeof val !== "object") {
      return t("exam_result_page.placeholder");
    }

    const regexes = Array.isArray(item?.body?.clozeRegexes)
      ? item.body.clozeRegexes
      : [];

    const keys = Object.keys(val).sort((a, b) => Number(a) - Number(b));

    if (!keys.length) {
      return t("exam_result_page.placeholder");
    }

    const parts = keys.map((k, idx) => {
      const regDef = regexes[idx];
      const label = regDef?.placeholder?.fa || regDef?.placeholder?.en || k;

      const v = val[k];
      const shown = v != null && String(v).trim() ? String(v) : "…";

      return `${label}: ${shown}`;
    });

    // مثال: "Blank 1: foo | Blank 2: bar"
    return parts.join(" | ");
  }
  if (kind === "true_false") {
    // نرمال‌سازی مقدار ذخیره‌شده
    let raw;
    if (
      typeof val === "string" ||
      typeof val === "number" ||
      typeof val === "boolean"
    ) {
      raw = String(val).trim().toLowerCase();
    } else if (isObj(val)) {
      raw = String(val.value ?? val.selected ?? "")
        .trim()
        .toLowerCase();
    } else {
      return t("exam_result_page.placeholder");
    }

    if (raw === "true") return t("truefalse.true");
    if (raw === "false") return t("truefalse.false");
    return t("exam_result_page.placeholder");
  }
  if (kind === "ordering") {
    const itemsArr = Array.isArray(item?.body?.items) ? item.body.items : [];

    if (!val || (!Array.isArray(val) && !Array.isArray(val?.order))) {
      return t("exam_result_page.placeholder");
    }

    let seq = [];
    if (Array.isArray(val)) {
      seq = val;
    } else if (Array.isArray(val.order)) {
      seq = val.order;
    }

    seq = seq.map((x) => String(x));

    if (!seq.length) {
      return t("exam_result_page.placeholder");
    }

    const parts = seq.map((k) => {
      const idx = Number(k);
      const it = Number.isFinite(idx) && itemsArr[idx] ? itemsArr[idx] : null;
      const label = it?.label?.fa || it?.label?.en || String(k);
      return label;
    });

    return parts.join(" \u2192 ");
  }

  if (kind === "image_matching") {
    if (!val || typeof val !== "object")
      return t("exam_result_page.placeholder");

    const pairs = Array.isArray(item?.body?.pairs) ? item.body.pairs : [];
    if (!pairs.length) return t("exam_result_page.placeholder");

    const raw =
      val.matches && typeof val.matches === "object" ? val.matches : val;

    const parts = pairs.map((p, idx) => {
      const imgLabel =
        p?.image?.alt?.fa ||
        p?.image?.alt?.en ||
        p?.label?.fa ||
        p?.label?.en ||
        p?.image?.url ||
        `#${idx + 1}`;

      const key = String(idx);
      const chosenKey =
        raw && Object.prototype.hasOwnProperty.call(raw, key)
          ? String(raw[key])
          : null;

      let chosenLabel = "…";
      if (chosenKey != null) {
        const chosenIndex = Number(chosenKey);
        const pairForLabel =
          Number.isFinite(chosenIndex) && pairs[chosenIndex]
            ? pairs[chosenIndex]
            : null;
        if (pairForLabel) {
          chosenLabel =
            pairForLabel?.label?.fa ||
            pairForLabel?.label?.en ||
            `#${chosenIndex + 1}`;
        } else {
          chosenLabel = chosenKey;
        }
      }

      return `${imgLabel}: ${chosenLabel}`;
    });

    return parts.join(" | ");
  }

  if (kind === "drag_to_group") {
    if (!val || typeof val !== "object")
      return t("exam_result_page.placeholder");

    const items = Array.isArray(item?.body?.items) ? item.body.items : [];
    const groups = Array.isArray(item?.body?.groups) ? item.body.groups : [];
    if (!items.length || !groups.length)
      return t("exam_result_page.placeholder");

    const groupLabelMap = new Map(
      groups.map((g) => [
        String(g.key),
        g?.label?.fa || g?.label?.en || String(g.key),
      ])
    );

    const raw =
      val.assignments && typeof val.assignments === "object"
        ? val.assignments
        : val;

    const parts = items.map((it) => {
      const itemKey = String(it.key);
      const itemLabel = it?.label?.fa || it?.label?.en || itemKey;
      const chosenKey =
        raw && Object.prototype.hasOwnProperty.call(raw, itemKey)
          ? String(raw[itemKey])
          : null;
      const groupLabel = chosenKey
        ? groupLabelMap.get(chosenKey) || chosenKey
        : "…";
      return `${itemLabel}: ${groupLabel}`;
    });

    return parts.join(" | ");
  }

  if (kind === "matching") {
    if (!val || typeof val !== "object")
      return t("exam_result_page.placeholder");

    const pairs = Array.isArray(item?.body?.pairs) ? item.body.pairs : [];
    if (!pairs.length) return t("exam_result_page.placeholder");

    const raw =
      val.matches && typeof val.matches === "object" ? val.matches : val;

    const rightLabelMap = new Map(
      pairs.map((p, idx) => [
        String(idx),
        p?.right?.fa || p?.right?.en || String(idx),
      ])
    );

    const parts = pairs.map((p, idx) => {
      const leftLabel = p?.left?.fa || p?.left?.en || String(idx + 1 || "");
      const k = String(idx);
      const chosenKey =
        raw && Object.prototype.hasOwnProperty.call(raw, k)
          ? String(raw[k])
          : null;
      const rightLabel = chosenKey
        ? rightLabelMap.get(chosenKey) || chosenKey
        : "…";
      return `${leftLabel}: ${rightLabel}`;
    });

    return parts.join(" | ");
  }

  if (kind === "ordering_weighted") {
    const itemsArr = Array.isArray(item?.body?.items) ? item.body.items : [];

    let seq = [];
    if (isObj(val) && Array.isArray(val.order)) {
      seq = val.order;
    } else if (Array.isArray(val)) {
      seq = val;
    } else {
      return t("exam_result_page.placeholder");
    }

    seq = seq.map((x) => String(x));
    if (!seq.length) {
      return t("exam_result_page.placeholder");
    }

    const parts = seq.map((k) => {
      const idx = Number(k);
      const it = Number.isFinite(idx) && itemsArr[idx] ? itemsArr[idx] : null;
      const label = it?.label?.fa || it?.label?.en || String(k);
      return label;
    });

    // خروجی چیزی شبیه:
    // "Most important → Medium → Less important"
    return parts.join(" \u2192 ");
  }

  if (kind === "mcq_matrix") {
    if (!val || typeof val !== "object")
      return t("exam_result_page.placeholder");

    const rows = Array.isArray(item?.body?.rows) ? item.body.rows : [];
    const cols = Array.isArray(item?.body?.cols) ? item.body.cols : [];
    const colLabelMap = new Map(
      cols.map((c) => [
        String(c.key),
        c?.label?.fa || c?.label?.en || String(c.key),
      ])
    );

    const map =
      val.selections && typeof val.selections === "object"
        ? val.selections
        : val;

    const parts = rows.map((r) => {
      const rk = String(r.key);
      const rowLabel = r?.label?.fa || r?.label?.en || rk;
      const raw = map[rk];

      let label = "…";

      if (Array.isArray(raw)) {
        if (raw.length) {
          const labels = raw.map(
            (k) => colLabelMap.get(String(k)) || String(k)
          );
          label = labels.join(", ");
        }
      } else if (raw != null) {
        label = colLabelMap.get(String(raw)) || String(raw);
      }

      return `${rowLabel}: ${label}`;
    });

    return parts.join(" | ");
  }

  if (kind === "sentence_reconstruction") {
    const tmpl =
      item?.body?.text?.en || item?.body?.text?.fa || item?.body?.text || "";
    const userTokens = Array.isArray(val)
      ? val
      : Array.isArray(val?.tokens)
      ? val.tokens
      : [];
    if (!tmpl || !userTokens.length) return t("exam_result_page.placeholder");
    return fillTemplateWithTokens(tmpl, userTokens);
  }

  if (kind === "c_test") {
    // انتظار: val = { "1":"uage", "2":"ires", ... }
    if (!val || typeof val !== "object")
      return t("exam_result_page.placeholder");
    const textHtml =
      item?.body?.text?.en || item?.body?.text?.fa || item?.body?.text || "";
    const order = extractCtestKeyOrder(textHtml); // ["1","2","3",...]
    const parts = order.map(
      (k) => `${k}: ${val[k] != null ? String(val[k]) : "…"}`
    );
    return parts.join(" | ");
  }

  if (kind === "passage_mcq") {
    if (isObj(val) && Number.isFinite(val.slot)) {
      return `#${val.slot}`;
    }
    return t("exam_result_page.placeholder");
  }

  if (kind === "audio_prompt") {
    return ``;
  }

  if (kind === "writing_prompt") {
    return `${val?.text?.slice(0, 100)}...`;
  }

  if (kind === "essay") {
    if (isObj(val) && typeof val.text === "string") {
      return val.text;
    }
    return t("exam_result_page.placeholder");
  }

  if (kind?.startsWith("speaking")) {
    if (isObj(val) && typeof val.text === "string" && val.text.trim()) {
      return val.text.trim();
    }
    if (isObj(val) && isObj(val.recording) && val.recording.url) {
      return "[recording]";
    }
    return t("exam_result_page.placeholder");
  }

  if (kind === "mcq_single" || kind === "mcq_multi") {
    if (hasSelected(val)) {
      const sel = val.selected;
      return Array.isArray(sel)
        ? sel.join(", ")
        : String(sel ?? t("exam_result_page.placeholder"));
    }
    return val != null ? String(val) : t("exam_result_page.placeholder");
  }

  if (hasSelected(val)) {
    const sel = val.selected;
    if (Array.isArray(sel))
      return sel.map((k) => renderChoice(item, k, "en", t)).join(", ");
    return renderChoice(item, sel, "en", t);
  }

  if (Array.isArray(val)) {
    return val.map((v) => String(v)).join(" \u2192 ");
  }

  if (
    typeof val === "string" ||
    typeof val === "number" ||
    typeof val === "boolean"
  ) {
    return String(val);
  }

  try {
    return val ? JSON.stringify(val) : t("exam_result_page.placeholder");
  } catch {
    return t("exam_result_page.placeholder");
  }
};
